import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

interface RequireGroupProps {
  group: "super-admin" | "normal_user";
  children: ReactNode;
}

/**
 * RequireGroup guards routes by Cognito group:
 * - "super-admin": only users in super-admin group
 * - "normal_user": users in normal_user OR super-admin (admins can also view employee area)
 */
export function RequireGroup({ group, children }: RequireGroupProps) {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isSuperAdmin = user.groups.includes("super-admin");
  const isNormalUser = user.groups.includes("normal_user");

  if (group === "super-admin" && !isSuperAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (group === "normal_user" && !isNormalUser && !isSuperAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

/**
 * Guards routes that require either super-admin OR being a business admin of
 * the currently active business. Used for per-business management pages
 * (e.g. Manage Roles) that business admins should access.
 */
export function RequireSuperAdminOrBizAdmin({ children }: { children: ReactNode }) {
  const { user, isActiveBusinessAdmin } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  const isSuperAdmin = user.groups.includes("super-admin");
  if (!isSuperAdmin && !isActiveBusinessAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
